/**
 * AI Artifact Dashboard Module
 *
 * Displays AI-generated artifacts (plots, tables) from AI Teammate pinned workflows.
 */

import { useCallback, useContext, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias } from "resources/account"
import { useResourceCurrency } from "resources/currencies"
import RestDataIntegrator from "gatherers/rest-data-integrator"
import { IDashboardContextValue } from "connected-components/dashboard/context/types"
import { useModules } from "../../context/modules"
import ArtifactRenderer from "renderers/ai-artifact"
import { ERROR, OK } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import type { ModuleRenderDependencies } from "../types"
import { createArtifactGatherer } from "./gatherer"
import type { AgentArguments, Artifact, PositionSet } from "./types"
import type { GridInitialState } from "@mui/x-data-grid-premium"
import { NotFoundError } from "api/errors"

import "./style.scss"

function unwrapSingleQueryResult<T>(data: T[]): T | null {
  return Array.isArray(data) && data.length > 0 ? data[0] : null
}

function getPositionSetWithAlias(
  selectedPositionSet: PositionSet | undefined,
  alias: string | undefined,
): PositionSet | undefined {
  if (!selectedPositionSet) return undefined

  return {
    ...selectedPositionSet,
    id: alias || selectedPositionSet.id,
  }
}

/**
 * AI Artifact Module Component
 *
 * Fetches and displays AI-generated artifacts using RestDataIntegrator
 * and delegates rendering to artifact templates based on kind.
 */
export default function AiArtifactModule({
  TradeContext,
  moduleId,
}: Omit<ModuleRenderDependencies, "config"> & { moduleId: string }) {
  const queryClient = useQueryClient()
  const accountAlias = useAccountAlias()
  const { modules, updateModule, removeModule } = useModules()
  const dashboardContext = useContext(TradeContext) as IDashboardContextValue
  const { modelId, startDate, endDate, base, instrument, instrumentsMetadata } = dashboardContext
  const [currency] = useResourceCurrency(modelId, instrument)

  // Get the current module config from context instead of props
  // This ensures we always have the latest config, even when it updates
  const module = useMemo(
    () => modules.find(m => m.id === moduleId),
    [modules, moduleId],
  )
  const config = module?.config || {}
  const { pinnedId, kind, isPinning } = config
  const title = typeof config.title === "string" ? config.title.trim() : ""

  const agentArguments: AgentArguments = useMemo(() => {
    const originalMetadata = instrumentsMetadata?.original
    const instrumentAlias = originalMetadata && "alias" in originalMetadata
      ? originalMetadata.alias
      : undefined

    return {
      modelId,
      // Type assertion: In practice, instrument and base are always SelectedTypedInstrument (with id/type)
      // when used with AI artifacts, never SelectedInlineInstrument or SelectedRulesInstrument
      positionSet: getPositionSetWithAlias(instrument as PositionSet, instrumentAlias),
      base: base as PositionSet | undefined,
      startDate,
      endDate,
    }
  }, [modelId, instrument, instrumentsMetadata, base, startDate, endDate])

  // Create gatherer configuration when pinnedId is available
  const gatherer = useMemo(() => {
    if (!pinnedId) return null
    return createArtifactGatherer({
      pinnedId,
      agentArguments,
      accountAlias,
      kind,
    })
  }, [pinnedId, agentArguments, accountAlias, kind])

  // Memoize queries array to prevent unnecessary re-renders
  const queries = useMemo(() => {
    return gatherer ? [gatherer] : []
  }, [gatherer])

  const handleRetry = useCallback(() => {
    if (!gatherer) return
    queryClient.invalidateQueries({ queryKey: gatherer.key })
  }, [gatherer, queryClient])

  const onDataGridStateChange = useCallback(
    (dataGridState: GridInitialState) => {
      if (!module) return
      updateModule(module.id, { config: { dataGridState } })
    },
    [module, updateModule],
  )

  const rendererProps = useMemo(
    () => ({
      currency,
      title: title || undefined,
      dataGridState: config.dataGridState as GridInitialState | undefined,
      isPinning,
      onDataGridStateChange,
      onRetry: handleRetry,
    }),
    [currency, title, config.dataGridState, isPinning, onDataGridStateChange, handleRetry],
  )

  const onStatusChange = useCallback((status: DataStatus) => {
    if (!module) return

    if (status === ERROR && config.hasError !== true) {
      updateModule(module.id, { config: { hasError: true } })
      return
    }

    if (status === OK && config.hasError) {
      updateModule(module.id, { config: { hasError: false } })
    }
  }, [config.hasError, module, updateModule])

  const handleQueryError = useCallback((error: unknown) => {
    if (error instanceof NotFoundError && module) {
      removeModule(module)
    }
  }, [module, removeModule])

  // If pinning has already completed, missing gatherer/pinnedId is a terminal error.
  if (!isPinning && (!pinnedId || !gatherer)) {
    return <div className="ai-artifact-module__error">We couldn&apos;t load this module. Please try removing and pinning it again.</div>
  }

  return (
    <div className="ai-artifact-module">
      <RestDataIntegrator<Artifact>
        queries={queries}
        Renderer={ArtifactRenderer}
        rendererProps={rendererProps}
        dataTransform={unwrapSingleQueryResult}
        onStatusChange={onStatusChange}
        onError={handleQueryError}
        key={`artifact-${moduleId}-${pinnedId}`}
      />
    </div>
  )
}
